import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { getAllLanguageOptionsByLangCode } from 'lib/courses';
import { PageSideNav } from 'components/page';

interface Props {
  categories: {
    id: number;
    title: string;
  }[];
  customAttributes?: {
    key: string;
    values: string[];
  }[];
  hasMandatoryCourses?: boolean;
  languages: string[];
}

const CoursesNav = ({
  categories,
  customAttributes,
  hasMandatoryCourses,
  languages
}: Props) => {
  const { messages, serverConfig, user } = useReduxState();

  const router = useRouter();

  const type = router.query?.type as string;
  const categoryId = router.query?.categoryId as string | string[];
  const language = router.query?.language as string | string[];
  const presentInAll = router.query?.presentInAll as string | string[];

  const languageOptions = getAllLanguageOptionsByLangCode();

  let preservedFilters = '';

  if (typeof categoryId === 'string') {
    preservedFilters += `&categoryId=${categoryId}`;
  } else if (typeof categoryId === 'object') {
    preservedFilters += categoryId.map((id) => `&categoryId=${id}`).join('');
  }

  if (typeof language === 'string') {
    preservedFilters += `&language=${language}`;
  } else if (typeof language === 'object') {
    preservedFilters += language.map((value) => `&language=${value}`).join('');
  }

  if (typeof presentInAll === 'string') {
    preservedFilters += `&presentInAll=${presentInAll}`;
  } else if (typeof presentInAll === 'object') {
    preservedFilters += presentInAll
      .map((value) => `&presentInAll=${value}`)
      .join('');
  }

  const categoryFilters =
    categories?.length && !serverConfig.useAlternativeCategoryFilter
      ? {
          [messages.commonCategories]: [
            ...categories.map((category) => ({
              label: category.title,
              value: `&categoryId=${category.id}`
            }))
          ]
        }
      : {};

  const languageFilters =
    languages?.length > 0
      ? {
          [messages.commonLanguage]:
            languages.map((language) => {
              const option = languageOptions[language];
              return {
                label: option?.capitalizedName,
                value: `&language=${option?.value}`
              };
            }) || []
        }
      : {};

  const mandatoryFilters =
    type !== 'available' && hasMandatoryCourses
      ? {
          [messages.commonMandatory]: [
            { label: messages.commonYes, value: '&isMandatory=true' },
            { label: messages.commonNo, value: '&isMandatory=false' }
          ]
        }
      : {};

  const filters = {
    ...(type === 'administered' && {
      [messages.commonStatus]: [
        { label: messages.commonPublished, value: '&publishStatus=published' },
        {
          label: messages.commonWillPublish,
          value: '&publishStatus=publishLater'
        },
        {
          label: messages.commonNotPublished,
          value: '&publishStatus=notPublished'
        }
      ]
    }),
    ...(type === 'active' && {
      [messages.commonStatus]: [
        { label: messages.commonCompleted, value: '&completionStatus=yes' },
        { label: messages.commonValid, value: '&completionStatus=valid' },
        { label: messages.commonExpiring, value: '&completionStatus=expiring' },
        { label: messages.commonExpired, value: '&completionStatus=expired' },
        { label: messages.commonFailed, value: '&completionStatus=failed' },
        {
          label: messages.commonInProgress,
          value: '&completionStatus=inProgress'
        },
        {
          label: messages.commonNotStarted,
          value: '&completionStatus=notStarted'
        }
      ]
    }),
    ...mandatoryFilters,
    ...(serverConfig.productFiltersPrioritizeLanguage
      ? { ...languageFilters, ...categoryFilters }
      : { ...categoryFilters, ...languageFilters })
  };

  const filtersSeparate = {
    ...(categories?.length &&
      serverConfig.useAlternativeCategoryFilter && {
        [messages.commonCategories]: [
          ...categories.map((category) => ({
            label: category.title,
            value: `&categoryId=${category.id}`
          }))
        ]
      })
  };

  const sections = [
    {
      links: [
        {
          href: '/courses?type=all' + preservedFilters,
          icon: 'list' as const,
          isActive: type === 'all',
          text: messages.commonAllCourses
        },
        ...(user?.roles.includes('admin')
          ? [
              {
                href: '/courses?type=administered' + preservedFilters,
                icon: 'edit' as const,
                isActive: type === 'administered',
                text: messages.coursesAdministered
              }
            ]
          : []),
        {
          href: '/courses?type=active' + preservedFilters,
          icon: 'book' as const,
          isActive: type === 'active',
          text: messages.coursesMyCourses
        },
        {
          href: '/courses?type=available' + preservedFilters,
          icon: 'book-open' as const,
          isActive: type === 'available',
          text: messages.commonAvailable
        }
      ]
    }
  ];

  return (
    <div className="nav">
      <PageSideNav
        filterCustomAttributes={customAttributes}
        filters={filters}
        filtersSeparate={filtersSeparate}
        filtersWithRequireAll={['categoryId', 'language']}
        linkSections={sections}
        shallowNavigation={true}
      />

      <style jsx>{`
        .nav {
          display: flex;
        }
      `}</style>
    </div>
  );
};

export default CoursesNav;
